import React, { FC } from 'react';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { Box } from '~Styles';
import { handyBreakpoint } from '~Styles/breakpoints';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { useDispatch, useI18n } from '~Hooks';
import * as actions from '~actions';
import { SETTINGS_ROW_MIN_HEIGHT } from '~ComponentsImproved/settingsRowMinHeight';

const RadioButtonContainer = styled.div<{
  disabled: boolean;
  disablePointerEvents?: boolean;
  compact: boolean;
}>`
  font-size: 20px;
  min-height: ${({ compact }) => (compact ? 'auto' : `${SETTINGS_ROW_MIN_HEIGHT}px`)};
  box-sizing: border-box;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'auto')};
  pointer-events: ${({ disablePointerEvents }) => (disablePointerEvents ? 'none' : 'auto')};

  h5 {
    line-height: normal;
  }
`;

const InnerContainer = styled.div<{ compact: boolean }>`
  padding: ${({ compact }) => (compact ? '8px 8px' : '12px 16px')};
  display: flex;
  /* On handheld the options go on their own row under the label */
  flex-flow: ${({ compact }) => (compact ? 'column nowrap' : 'row wrap')};
  justify-content: space-between;
  gap: 8px;
`;

const RadioButtonGroup = styled.div<{ compact: boolean }>`
  font-size: 16px;
  font-weight: 500;
  text-overflow: ellipsis;
  display: flex;
  flex-direction: row;
  flex-flow: wrap;
  gap: 10px;
  color: var(--colorWhite);

  ${handyBreakpoint} {
    flex-direction: column;
    gap: 16px;

    & > div {
      gap: 8px;
    }
  }

  /* Compact rows keep the options side by side, wrapping when needed */
  ${({ compact }) =>
    compact &&
    `
    flex-direction: row;
    gap: 8px 16px;
  `}
`;

const RadioButton = styled.input`
  font-size: 20px;
  gap: 10px;
  height: 20px;
  width: 20px;
  border: none;
  vertical-align: middle;
  accent-color: var(--colorUIGreen);
`;

/** The whole option (circle + text) is one tap target */
const Option = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 16px;
`;

interface RadioButtonOption {
  id: string;
  label: string;
  value: any;
}

interface Props {
  data: {
    containerLabel: string;
    containerDesc?: string;
    name: string;
    buttons: RadioButtonOption[];
  };
  selected: string;
  handleChange: (value: any, id: string) => void;
  /** If provided, this component is rendered in Settings and we need to check if it's remotely controlled */
  settingKey?: string;
  /** Manually disable the selector (e.g. because another setting makes this one irrelevant) */
  disabled?: boolean;
  /** Tighter padding for handheld screens */
  compact?: boolean;
}

const RadioButtonSelect: FC<Props> = ({
  data,
  selected,
  handleChange,
  settingKey,
  disabled = false,
  compact = false,
}) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const deviceHandler = useDeviceHandler();

  const isRemotelyControlled = settingKey && deviceHandler.remotelyControlledData[settingKey];
  const isDisabled = Boolean(isRemotelyControlled) || disabled;

  const isHandy = window.innerWidth <= 600;

  const onChange = (value: any, id: string) => {
    /** We don't have space to display a paragraph so we show a toast if clicked instead informing that it's remotely controlled */
    if (isDisabled && isHandy) {
      dispatch(actions.addToast(i18n('Settings.LockedByBaemingo.Toast'), true));

      return;
    }

    handleChange(value, id);
  };

  return (
    <RadioButtonContainer
      disabled={isDisabled}
      disablePointerEvents={isDisabled && !isHandy}
      compact={compact}>
      <InnerContainer compact={compact}>
        <Text>
          <Heading level={5} color="var(--colorWhite)">
            {data.containerLabel}
          </Heading>
          {data.containerDesc && (
            <Paragraph level={5} color="var(--colorBlueGray300)">
              {data.containerDesc}
            </Paragraph>
          )}
        </Text>

        <Box
          display="flex"
          flexDirection="column"
          alignItems={compact ? 'flex-start' : 'flex-end'}
          gap="xs">
          <RadioButtonGroup compact={compact}>
            {data.buttons.map((button) => (
              <Option key={button.id} onClick={() => onChange(button.value, button.id)}>
                {/* Clicks are handled by the label so the text works as a target too; the input only reflects state */}
                <RadioButton
                  type="radio"
                  name={data.name}
                  checked={selected === button.id}
                  readOnly
                />
                {button.label}
              </Option>
            ))}
          </RadioButtonGroup>
          {!isHandy && Boolean(isRemotelyControlled) && (
            <Paragraph level={5} color="var(--colorBlueGray300)">
              ({i18n('Settings.LockedByBaemingo')})
            </Paragraph>
          )}
        </Box>
      </InnerContainer>
    </RadioButtonContainer>
  );
};

export default RadioButtonSelect;
